/* PDF del Overview: las mismas cifras que se ven en pantalla, presentadas como tablas.
   Se arma a partir de las respuestas que el servidor ya devolvió (filtradas por sucursales, grupos, eventos, fechas y
   permisos del usuario), así que el archivo no puede mostrar más ni distintos datos que la pantalla. */

import { METRICS } from '../data/catalog';
import { buildConsolidatedPdf, formatMoney } from './consolidatedTable';

const countFormat = new Intl.NumberFormat('es-PA', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// Gráficos que muestran una sola cifra grande: van juntos en la tabla de indicadores del bloque
const SCALAR_CHARTS = ['kpi', 'stat_delta', 'ring'];
const BRANCH_METRICS = ['ventas_por_sucursal', 'ventas_sucursal', 'sales_by_branch'];
const BY_PERIOD_METRICS = ['ventas', 'total_ventas', 'ordenes', 'orders', 'ticket', 'ticket_promedio', 'clientes', 'clientes_activos'];

const GRAY = '#6b7280';
const HEADER_FILL = '#111827';
const TOTAL_FILL = '#F3F4F6';

export const formatByUnit = (value, unit) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return value === undefined || value === null ? '' : String(value);
  if (unit === 'currency') return formatMoney(number);
  if (unit === '%') return `${countFormat.format(number)}%`;
  return countFormat.format(number);
};

const metricLabel = (slot, entry) => {
  const known = METRICS.find((metric) => metric.id === slot.metricId);
  return (known && known.label) || (entry && entry.title) || slot.metricId;
};

const changeColor = (entry) => {
  const pct = Number(entry.delta_percentage);
  if (pct < 0) return '#dc2626';
  return pct > 0 ? '#16a34a' : GRAY;
};

const formatChange = (entry) => {
  if (entry.delta_percentage === null || entry.delta_percentage === undefined) return '—';
  const pct = Number(entry.delta_percentage);
  if (!Number.isFinite(pct)) return '—';
  return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
};

const seriesOf = (entry) => (Array.isArray(entry.data)
  ? entry.data
    .filter((item) => item && typeof item === 'object' && (item.label !== undefined || item.name !== undefined))
    .map((item) => ({ label: String(item.label ?? item.name), value: item.value ?? item.v }))
  : []);

const stateMessage = (state) => {
  if (!state) return 'Sin información';
  if (state.blocked) return state.blocked;
  if (state.error) return state.error;
  if (state.noData || !state.data) return 'No hay información para este filtro';
  return null;
};

const cell = (text, extra = {}) => ({ text: String(text), ...extra });
const headerCell = (text, alignment = 'left') => ({ text, bold: true, color: '#ffffff', fillColor: HEADER_FILL, alignment });

const simpleTable = (header, rows, { widths, totalRow } = {}) => ({
  table: {
    headerRows: 1,
    widths: widths || header.map((_, index) => (index === 0 ? '*' : 'auto')),
    body: [
      header,
      ...rows,
      ...(totalRow ? [totalRow.map((item) => ({ ...item, bold: true, fillColor: TOTAL_FILL }))] : []),
    ],
  },
  layout: 'lightHorizontalLines',
  fontSize: 9,
  margin: [0, 0, 0, 10],
});

const noteLine = (text) => ({ text, fontSize: 9, color: GRAY, italics: true, margin: [0, 0, 0, 10] });

// Serie por fecha: dos columnas (período y valor), una fila por período y el total al final
const seriesTable = (rows, unit, firstHeader, totalValue) => {
  const total = totalValue === undefined || totalValue === null
    ? null
    : [cell('Total'), cell(formatByUnit(totalValue, unit), { alignment: 'right' })];

  return simpleTable(
    [headerCell(firstHeader), headerCell('Valor', 'right')],
    rows.map((item) => [cell(item.label), cell(formatByUnit(item.value, unit), { alignment: 'right' })]),
    { totalRow: total },
  );
};

const branchTables = (entry) => {
  const unit = entry.unit || 'currency';
  const rows = seriesOf(entry);
  const blocks = [];
  blocks.push(simpleTable(
    [headerCell('Sucursal'), headerCell('Ventas', 'right')],
    rows.map((item) => [cell(item.label), cell(formatByUnit(item.value, unit), { alignment: 'right' })]),
    { totalRow: [cell('Total'), cell(formatByUnit(entry.value, unit), { alignment: 'right' })] },
  ));

  if (Array.isArray(entry.groups) && entry.groups.length > 0) {
    blocks.push({ text: 'Por grupo de sucursales', style: 'widgetTitle' });
    blocks.push(simpleTable(
      [headerCell('Grupo'), headerCell('Sucursales', 'right'), headerCell('Ventas', 'right')],
      entry.groups.map((group) => [
        cell(group.name),
        cell(group.branches_count, { alignment: 'right' }),
        cell(formatByUnit(group.value, unit), { alignment: 'right' }),
      ]),
      { widths: ['*', 'auto', 'auto'] },
    ));
    blocks.push(noteLine('Una sucursal que está en dos grupos cuenta en cada uno; por eso los grupos pueden sumar más que el total.'));
  }

  if (Array.isArray(entry.events) && entry.events.length > 0) {
    blocks.push({ text: 'Por evento', style: 'widgetTitle' });
    blocks.push(simpleTable(
      [headerCell('Evento'), headerCell('Ventas', 'right')],
      entry.events.map((event) => [cell(event.name), cell(formatByUnit(event.value, unit), { alignment: 'right' })]),
      { totalRow: [cell('Total'), cell(formatByUnit(entry.events.reduce((sum, event) => sum + Number(event.value || 0), 0), unit), { alignment: 'right' })] },
    ));
  }
  return blocks;
};

const widgetBlocks = (slot, state) => {
  const entry = state && state.data;
  const label = metricLabel(slot, entry);
  const title = { text: label, style: 'widgetTitle' };
  const message = stateMessage(state);
  if (message) return [title, noteLine(message)];

  if (slot.chartType === 'table' && Array.isArray(entry.columns)) {
    const consolidated = buildConsolidatedPdf({ entry, title: label });
    // Solo la tabla: el encabezado general del documento ya describe los filtros
    return [title, consolidated.content[consolidated.content.length - 1]];
  }

  if (BRANCH_METRICS.includes(slot.metricId)) return [title, ...branchTables(entry)];

  const rows = seriesOf(entry);
  if (rows.length === 0) {
    return [title, simpleTable([headerCell('Indicador'), headerCell('Valor', 'right')], [[cell(label), cell(formatByUnit(entry.value, entry.unit), { alignment: 'right' })]])];
  }
  const firstHeader = BY_PERIOD_METRICS.includes(slot.metricId) ? 'Período' : 'Detalle';
  return [title, seriesTable(rows, entry.unit, firstHeader, entry.value)];
};

/* Tabla de indicadores de un bloque: nombre, valor y cambio frente al período anterior (si el widget lo calcula) */
const indicatorsTable = (items) => simpleTable(
  [headerCell('Indicador'), headerCell('Valor', 'right'), headerCell('Cambio vs. período anterior', 'right')],
  items.map(({ label, entry, message }) => (message
    ? [cell(label), cell(message, { colSpan: 2, color: GRAY, italics: true }), cell('')]
    : [
      cell(label),
      cell(formatByUnit(entry.value, entry.unit), { alignment: 'right', bold: true }),
      cell(formatChange(entry), { alignment: 'right', color: changeColor(entry) }),
    ])),
  { widths: ['*', 'auto', 'auto'] },
);

/* Orden de lectura del PDF: primero las cifras sueltas (indicadores), luego los desgloses cortos (sucursales, grupos,
   tabla consolidada) y al final las series largas por fecha, que ocupan más hojas. Dentro de un mismo nivel se respeta
   el orden del Overview. Un bloque con varios widgets toma el nivel del más largo. */
const SECTION_RANK = { indicators: 0, breakdown: 1, series: 2 };
const slotRank = ({ slot }) => {
  if (SCALAR_CHARTS.includes(slot.chartType)) return SECTION_RANK.indicators;
  if (slot.chartType === 'table' || BRANCH_METRICS.includes(slot.metricId)) return SECTION_RANK.breakdown;
  return SECTION_RANK.series;
};

/* Definición del PDF (pdfMake): vertical, encabezado con los filtros aplicados y un bloque por cada bloque del Overview */
export const buildOverviewPdf = ({ layouts = [], metricData = {}, scopeLines = [], title = 'Overview', generatedAt = new Date() }) => {
  const content = [
    { text: title, style: 'title' },
    ...scopeLines.map(([label, value]) => ({ text: [{ text: `${label}: `, bold: true }, value], fontSize: 10, margin: [0, 0, 0, 2] })),
    { text: `Generado: ${generatedAt.toLocaleString('es-PA')}`, fontSize: 9, color: GRAY, margin: [0, 4, 0, 14] },
  ];

  const sections = [];
  layouts.forEach((layout, order) => {
    const layoutId = layout.id || layout._id;
    const slots = (layout.slots || [])
      .map((slot, slotIndex) => ({ slot, state: metricData[`${layoutId}:${slotIndex}`] }))
      .filter(({ slot }) => slot && slot.metricId && slot.chartType);
    if (slots.length === 0) return;

    const blocks = [{ text: layout.name || 'Bloque', style: 'blockTitle' }];

    const scalars = slots.filter(({ slot }) => SCALAR_CHARTS.includes(slot.chartType));
    if (scalars.length > 0) {
      blocks.push(indicatorsTable(scalars.map(({ slot, state }) => {
        const message = stateMessage(state);
        return { label: metricLabel(slot, state && state.data), entry: state && state.data, message };
      })));
    }
    slots.filter(({ slot }) => !SCALAR_CHARTS.includes(slot.chartType))
      .forEach(({ slot, state }) => blocks.push(...widgetBlocks(slot, state)));

    sections.push({ order, rank: Math.max(...slots.map(slotRank)), blocks });
  });

  sections
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
    .forEach((section) => content.push(...section.blocks));

  if (sections.length === 0) content.push(noteLine('No hay información para mostrar.'));

  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [32, 32, 32, 40],
    content,
    footer: (currentPage, pageCount) => ({ text: `${currentPage} / ${pageCount}`, alignment: 'center', fontSize: 8, color: GRAY }),
    styles: {
      title: { fontSize: 20, bold: true, margin: [0, 0, 0, 8] },
      blockTitle: { fontSize: 14, bold: true, margin: [0, 10, 0, 6] },
      widgetTitle: { fontSize: 11, bold: true, margin: [0, 4, 0, 4] },
    },
  };
};

/* ¿Alguna de las métricas que se ven ahora en pantalla sigue cargando? Solo cuentan los widgets actuales: el estado de
   widgets que ya no existen (un bloque eliminado, la plantilla que se reemplazó por el dashboard guardado) puede haber
   quedado en «cargando» y no debe bloquear la descarga. */
export const hasPendingMetrics = (layouts = [], metricData = {}) => layouts.some((layout) => {
  const layoutId = layout.id || layout._id;
  return (layout.slots || []).some((slot, slotIndex) => slot && slot.metricId && slot.chartType
    && Boolean(metricData[`${layoutId}:${slotIndex}`]?.loading));
});

export const downloadPdf = async (doc, filename) => {
  const [{ default: pdfMake }, { default: pdfFonts }] = await Promise.all([import('pdfmake/build/pdfmake'), import('pdfmake/build/vfs_fonts')]);
  pdfMake.vfs = pdfFonts.pdfMake.vfs;
  pdfMake.createPdf(doc).download(filename);
};
